import React, { createContext, useContext, useState, useEffect } from 'react';

const FontContext = createContext();

export const useFont = () => {
  const context = useContext(FontContext);
  if (!context) {
    throw new Error('useFont must be used within FontProvider');
  }
  return context;
};

export const fonts = [
  { 
    id: 'bebas', 
    name: 'Bebas Neue (Default)', 
    fontFamily: "'Bebas Neue', sans-serif",
    category: 'Display',
    googleFont: 'Bebas+Neue:wght@400'
  },
  { 
    id: 'inter', 
    name: 'Inter', 
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    category: 'Modern Sans'
  },
  { 
    id: 'poppins', 
    name: 'Poppins', 
    fontFamily: "'Poppins', sans-serif",
    category: 'Geometric Sans',
    googleFont: 'Poppins:wght@400;500;600;700;800;900'
  },
  { 
    id: 'montserrat', 
    name: 'Montserrat', 
    fontFamily: "'Montserrat', sans-serif",
    category: 'Geometric Sans',
    googleFont: 'Montserrat:wght@400;500;600;700;800;900'
  },
  { 
    id: 'oswald', 
    name: 'Oswald', 
    fontFamily: "'Oswald', sans-serif",
    category: 'Condensed',
    googleFont: 'Oswald:wght@400;500;600;700'
  },
  { 
    id: 'raleway', 
    name: 'Raleway', 
    fontFamily: "'Raleway', sans-serif",
    category: 'Elegant Sans',
    googleFont: 'Raleway:wght@400;500;600;700;800;900'
  },
  { 
    id: 'roboto', 
    name: 'Roboto', 
    fontFamily: "'Roboto', sans-serif",
    category: 'Neo-Grotesque',
    googleFont: 'Roboto:wght@400;500;700;900'
  },
  { 
    id: 'nunito', 
    name: 'Nunito', 
    fontFamily: "'Nunito', sans-serif",
    category: 'Rounded Sans',
    googleFont: 'Nunito:wght@400;600;700;800;900'
  },
  { 
    id: 'archivo', 
    name: 'Archivo Black', 
    fontFamily: "'Archivo Black', sans-serif",
    category: 'Heavy Display',
    googleFont: 'Archivo+Black:wght@400'
  },
  { 
    id: 'barlow', 
    name: 'Barlow', 
    fontFamily: "'Barlow', sans-serif",
    category: 'Grotesque Sans',
    googleFont: 'Barlow:wght@400;500;600;700;800;900'
  }
];

export const FontProvider = ({ children }) => {
  const [currentFont, setCurrentFont] = useState(fonts[0]); // Bebas Neue as default

  useEffect(() => {
    const saved = localStorage.getItem('font');
    if (saved) {
      const font = fonts.find(f => f.id === saved);
      if (font) setCurrentFont(font);
    }
  }, []);

  const changeFont = (fontId) => {
    const font = fonts.find(f => f.id === fontId);
    if (font) {
      setCurrentFont(font);
      localStorage.setItem('font', fontId);
      
      // Load Google Font if needed
      if (font.googleFont) {
        loadGoogleFont(font.googleFont);
      }
    }
  };

  const loadGoogleFont = (fontString) => {
    // Check if font is already loaded
    const existingLink = document.querySelector(`link[href*="${fontString.split(':')[0]}"]`);
    if (existingLink) return;

    // Create and append link element
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontString}&display=swap`;
    document.head.appendChild(link);
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--font-family', currentFont.fontFamily);
    
    // Load Google Font on mount if current font requires it
    if (currentFont.googleFont) {
      loadGoogleFont(currentFont.googleFont);
    }
  }, [currentFont]);

  return (
    <FontContext.Provider value={{ currentFont, changeFont, fonts }}>
      {children}
    </FontContext.Provider>
  );
};